import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ChevronLeft, Save, Users, Calculator, Car, Plus, Trash2,
    FileSpreadsheet, Share2, Search, CheckCircle,
    Building2, User, List, Download,
    PenTool, Mail, MessageCircle, Send, Loader2,
    MapPin, Home, Camera, FileText, X,
    ChevronDown, ChevronRight, ChevronUp, Sliders, Percent
} from 'lucide-react';
import { SettingsService, PreventiviService, ActivityTypeService, ClientService, FigureProfessionaliService } from '../services/apiService';
import { Client, SettingsDto, ActivityType, Preventivo, FiguraProfessionale, UserRole } from '../types';
import { clientiDellaSede } from '../utils/clientDisplay';
import { Modal } from '../components/Modal';
import { useToast } from "../components/ToastContext";
import { useAuth } from '../contexts/AuthContext';
import NumberInput from '../components/NumberInput';
import { generaPreventivoPdf, calcolaVoce, VocePreventivo } from '../utils/generaPreventivoPdf';
import { getClientDisplayName, getClientReferente, isClienteAzienda, getClientEmail, getClientTelefono } from '../utils/clientDisplay';

// VocePreventivo, MaggiorazioneApplicata, CalcoloVoce e calcolaVoce stanno in
// `src/utils/generaPreventivoPdf.ts`, condivisi con il generatore PDF (anche lato Node).

const fmtEur = (n: number) => `€ ${(Number(n) || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

type ActiveModal = 'NONE' | 'COLLAB' | 'KM' | 'SERVICES' | 'SHARE_OPTIONS' | 'SHARE_TYPE';

const ClientFilePreviewItem = ({ file, onRemove, onPreview }: { file: File; onRemove: () => void; onPreview: (f: File) => void }) => {
    const isImage = file.type.startsWith('image/');
    const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
    React.useEffect(() => {
        if (isImage) {
            const url = URL.createObjectURL(file);
            setPreviewUrl(url);
            return () => URL.revokeObjectURL(url);
        }
    }, [file, isImage]);
    return (
        <div className="flex items-center justify-between bg-white p-2 rounded-lg border border-gray-200 shadow-sm text-sm group">
            <div className="flex items-center gap-3 overflow-hidden flex-1 cursor-pointer" onClick={() => onPreview(file)}>
                {isImage && previewUrl ? (
                    <img src={previewUrl} alt={file.name} className="w-10 h-10 object-cover rounded border border-gray-200"/>
                ) : (
                    <div className="w-10 h-10 bg-gray-50 flex items-center justify-center rounded border border-gray-200">
                        <FileText size={20} className="text-gray-400"/>
                    </div>
                )}
                <span className="truncate font-medium text-gray-700 group-hover:text-blue-600 transition-colors">{file.name}</span>
            </div>
            <button type="button" onClick={(e) => { e.stopPropagation(); onRemove(); }} className="text-red-500 p-2 hover:bg-red-50 rounded-lg ml-2 transition-colors">
                <Trash2 size={16}/>
            </button>
        </div>
    );
};

export const NuovoPreventivoView: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams(); // Id preventivo in modifica
    const { addToast } = useToast();
    // Sede per un preventivo senza cliente in rubrica (o cliente senza sede): la usa solo il
    // responsabile, che ne ha più d'una; per gli altri il BE la ignora.
    const { currentSedeId, user } = useAuth();
    const sedeSelezionata = typeof currentSedeId === 'number' ? currentSedeId : undefined;

    // Nel nuovo preventivo (senza :id in URL) il primo download/condivisione di un tipo
    // (PREVENTIVO o PROFORMA) crea il record; qui ne teniamo l'id così i download
    // successivi aggiornano lo stesso record invece di crearne uno nuovo. La mappa
    // è per tipo, quindi preventivo e proforma restano documenti distinti.
    // In modifica vince sempre l'id dell'URL.
    const [persistedIds, setPersistedIds] = useState<{ PREVENTIVO?: number; PROFORMA?: number }>({});
    // Tipo del record aperto in modifica: una PROFORMA si aggiorna sul proprio record,
    // da un PREVENTIVO la proforma si genera come record a sé (/preventivi/{id}/proforma).
    const [loadedTipo, setLoadedTipo] = useState<'PREVENTIVO' | 'PROFORMA'>('PREVENTIVO');
    // Mandato o servizio a cui il preventivo aperto è collegato, con il cliente di allora:
    // cambiando cliente il server lo scollega, quindi si chiede conferma prima di salvare.
    const [collegamento, setCollegamento] = useState<{ clientId: number | null; descr: string } | null>(null);

    // Blocca l'autofill del browser sugli input del wizard. Come in NuovaPraticaView,
    // il MutationObserver interviene anche sugli input montati dopo (nuove voci).
    const formRootRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const root = formRootRef.current;
        if (!root) return;
        const patch = () => {
            root.querySelectorAll<HTMLElement>('input, select, textarea').forEach(el => {
                const tag = el.tagName.toLowerCase();
                const type = (el as HTMLInputElement).type;
                if (tag === 'input' && (type === 'checkbox' || type === 'radio' || type === 'file' || type === 'submit' || type === 'button')) return;
                el.setAttribute('autocomplete', tag === 'input' ? 'new-password' : 'off');
                el.setAttribute('autocorrect', 'off');
                el.setAttribute('autocapitalize', 'off');
                el.setAttribute('spellcheck', 'false');
                if (!el.getAttribute('data-1p-ignore')) el.setAttribute('data-1p-ignore', 'true');
                if (!el.getAttribute('data-lpignore')) el.setAttribute('data-lpignore', 'true');
            });
        };
        patch();
        const observer = new MutationObserver(patch);
        observer.observe(root, { childList: true, subtree: true });
        return () => observer.disconnect();
    }, []);

    // Stati base
    const [step, setStep] = useState<1 | 2>(1);
    // Aprendo un preventivo salvato si aspetta il caricamento, così non compare per un attimo
    // la scelta del cliente prima delle voci.
    const [caricamento, setCaricamento] = useState<boolean>(!!id);
    const [activeModal, setActiveModal] = useState<ActiveModal>('NONE');
    const [isSaving, setIsSaving] = useState(false);
    // Download Preventivo/Proforma in corso: blocca il doppio click (due record, due numeri).
    const [downloadingDoc, setDownloadingDoc] = useState<null | 'preventivo' | 'proforma'>(null);

    // Dati aziendali ed esterni
    const [globalSettings, setGlobalSettings] = useState<SettingsDto | null>(null);
    const [activitiesList, setActivitiesList] = useState<ActivityType[]>([]);
    const [clientsList, setClientsList] = useState<Client[]>([]);

    // Stati temporanei
    const [figureProfessionali, setFigureProfessionali] = useState<FiguraProfessionale[]>([]);
    const [figuraSetup, setFiguraSetup] = useState({ figuraId: '' as string | number, descrizione: '', ore: 4, costoInterno: 0, prezzoCliente: 0 });
    const [tempKm, setTempKm] = useState(100);

    // Cliente
    const [clientMode, setClientMode] = useState<'existing' | 'new'>('existing');
    const [selectedClient, setSelectedClient] = useState<Client | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [clientePdf, setClientePdf] = useState({
        denominazione: '', indirizzo: '', citta: '', cap: '', provincia: '',
        cfPiva: '', email: '', telefono: '',
    });
    const updateClientePdf = (field: string, value: string) => setClientePdf(prev => ({ ...prev, [field]: value }));

    // Preventivo / proforma
    const [voci, setVoci] = useState<VocePreventivo[]>([]);
    const [expandedVoci, setExpandedVoci] = useState<Set<string>>(new Set());
    const toggleExpanded = (id: string) => setExpandedVoci(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });
    const [note, setNote] = useState('');
    const [ivaPercentuale, setIvaPercentuale] = useState(22);
    const [tariffaKm, setTariffaKm] = useState<number>(0);
    const [statoPreventivo, setStatoPreventivo] = useState<string>('BOZZA');
    // Nasconde il box totali del preventivo (PDF e viste). Non vale per le proforma.
    const [nascondiTotale, setNascondiTotale] = useState(false);
    // Nasconde l'IVA: nel box totali solo il totale imponibile. Vale anche per le proforma.
    const [nascondiIva, setNascondiIva] = useState(false);

    // Caricamento dati
    useEffect(() => {
        const loadData = async () => {
            try {
                // Risorse base in parallelo
                const [settingsRes, activitiesRes, clientsRes, figureRes] = await Promise.all([
                    SettingsService.getAll(),
                    ActivityTypeService.getAll(),
                    ClientService.getAll(),
                    FigureProfessionaliService.getAll(),
                ]);

                // Settings
                if (settingsRes.success && settingsRes.data.length > 0) {
                    const s = settingsRes.data[0];
                    setGlobalSettings(s);
                    if (s.priceKM) setTariffaKm(s.priceKM);
                }

                // Attività
                if (activitiesRes.success) setActivitiesList(activitiesRes.data);

                // Figure professionali
                const rawFigure = figureRes?.data ?? figureRes;
                setFigureProfessionali(Array.isArray(rawFigure) ? rawFigure : []);

                // Clienti, qualunque sia la forma della risposta
                let clients: Client[] = [];
                const rawClients = (clientsRes as any)?.data ?? clientsRes;
                if (Array.isArray(rawClients)) clients = rawClients;
                else if (Array.isArray((rawClients as any)?.content)) clients = (rawClients as any).content;
                else if (Array.isArray((rawClients as any)?.data)) clients = (rawClients as any).data;
                setClientsList(clients);

                // In modifica (id presente)
                if (id) {
                    const prevRes = await PreventiviService.getById(id);
                    if (prevRes.success && prevRes.data) {
                        const p: Preventivo = prevRes.data;

                        // Dati preventivo
                        setStatoPreventivo((p.stato || 'BOZZA').toUpperCase());
                        setNote(p.notePerCliente || '');
                        setNascondiTotale(!!(p as any).nascondiTotale);
                        setNascondiIva(!!(p as any).nascondiIva);
                        setLoadedTipo((p as any).tipoDocumento === 'PROFORMA' ? 'PROFORMA' : 'PREVENTIVO');
                        const pa = p as any;
                        const descrLink = pa.praticaId ? `al mandato ${pa.praticaNome || ''}`.trim()
                            : pa.servizioFiduciarioId ? `al servizio ${pa.servizioFiduciarioNome || ''}`.trim() : null;
                        setCollegamento(descrLink ? { clientId: (pa.client || pa.cliente)?.id ?? null, descr: descrLink } : null);
                        // IVA fissa al 22%, ignoriamo l'eventuale percentuale salvata.

                        // Voci
                        try {
                            const vociData = typeof (p as any).vociJson === 'string' ? JSON.parse((p as any).vociJson) : (p.voci || []);
                            setVoci(vociData);
                        } catch (e) {
                            console.error("Errore parsing voci", e);
                        }

                        // Cliente: il backend serializza il campo `client`; teniamo il fallback su
                        // `cliente` per payload diversi.
                        const clienteFromBackend = (p as any).client || p.cliente;
                        if (clienteFromBackend) {
                            setClientMode('existing');
                            const fullClient = clients.find((c) => c.id === clienteFromBackend.id) || clienteFromBackend;
                            setSelectedClient(fullClient);
                            const saved = (p as any).clientePdfData || {};
                            setClientePdf({
                                denominazione: saved.denominazione || getClientDisplayName(fullClient) || `${fullClient.nome || ''} ${fullClient.surname || ''}`.trim(),
                                indirizzo: saved.indirizzo ?? (fullClient.indirizzo || ''),
                                citta: saved.citta ?? (fullClient.citta || ''),
                                cap: saved.cap ?? (fullClient.cap || ''),
                                provincia: saved.provincia ?? (fullClient.provincia || ''),
                                cfPiva: saved.cfPiva ?? (fullClient.codiceFiscale || fullClient.partitaIva || ''),
                                // Per le aziende il contatto del referente è nei campi *Referente: se il
                                // preventivo non è stato personalizzato mostriamo email e telefono della persona.
                                email: saved.email ?? getClientEmail(fullClient),
                                telefono: saved.telefono ?? getClientTelefono(fullClient),
                            });
                        } else {
                            setClientMode('new');
                            const saved = (p as any).clientePdfData || (p as any).tempClientData || {};
                            setClientePdf({
                                denominazione: saved.denominazione || saved.nome || p.tempClientName || '',
                                indirizzo: saved.indirizzo || '',
                                citta: saved.citta || '',
                                cap: saved.cap || '',
                                provincia: saved.provincia || '',
                                cfPiva: saved.cfPiva || saved.codiceFiscale || '',
                                email: saved.email || p.tempClientEmail || '',
                                telefono: saved.telefono || p.tempClientPhone || '',
                            });
                        }

                        // Passa direttamente allo step 2 (editor)
                        setStep(2);
                    }
                }

            } catch (error) {
                console.error("Errore caricamento dati:", error);
                addToast("Errore caricamento dati", "error");
            } finally {
                setCaricamento(false);
            }
        };
        loadData();
    }, [id]);

    // Totali (con maggiorazioni per voce)
    const totali = useMemo(() => {
        const imponibile = voci.reduce((acc, voce) => acc + calcolaVoce(voce, globalSettings).totale, 0);
        const iva = imponibile * (ivaPercentuale / 100);
        return { imponibile, iva, totale: imponibile + iva };
    }, [voci, ivaPercentuale, globalSettings]);

    // Filtro clienti
    // Solo i clienti della sede selezionata: niente dati di altre sedi nel menu.
    const filteredClients = useMemo(() => {
        const dellaSede = clientiDellaSede(clientsList, currentSedeId, user?.role === UserRole.ADMIN);
        if (!searchQuery) return dellaSede;
        const q = searchQuery.toLowerCase();
        return dellaSede.filter(c =>
            (c.nome && c.nome.toLowerCase().includes(q)) ||
            (c.surname && c.surname.toLowerCase().includes(q)) ||
            (c.ragioneSociale && c.ragioneSociale.toLowerCase().includes(q)) ||
            (c.email && c.email.toLowerCase().includes(q))
        );
    }, [searchQuery, clientsList, currentSedeId, user?.role]);

    // Gestione righe
    const addRiga = (riga: Partial<VocePreventivo>) => {
        setVoci([...voci, {
            id: Math.random().toString(36).substr(2, 9),
            descrizione: riga.descrizione || '',
            quantita: riga.quantita || 1,
            unita: riga.unita || 'forfait',
            prezzoUnitario: riga.prezzoUnitario || 0,
            scontoPercentuale: riga.scontoPercentuale || 0
        }]);
    };

    const updateRiga = (id: string, field: keyof VocePreventivo, value: any) => {
        setVoci(voci.map(v => v.id === id ? { ...v, [field]: value } : v));
    };

    const removeRiga = (id: string) => {
        setVoci(voci.filter(v => v.id !== id));
    };

    // Sposta la riga di una posizione (-1 su, +1 giù): l'ordine è quello del PDF.
    const spostaRiga = (index: number, delta: -1 | 1) => {
        const dest = index + delta;
        if (dest < 0 || dest >= voci.length) return;
        const next = [...voci];
        [next[index], next[dest]] = [next[dest], next[index]];
        setVoci(next);
    };

    // Helper modali
    const onSelectFigura = (figuraId: string) => {
        const figura = figureProfessionali.find(f => String(f.id) === figuraId);
        if (figura) {
            setFiguraSetup(prev => ({
                ...prev, figuraId,
                descrizione: figura.nome,
                costoInterno: figura.tariffa || 0,
                prezzoCliente: figura.prezzoCliente ?? figura.tariffa ?? 0,
            }));
        } else {
            setFiguraSetup(prev => ({ ...prev, figuraId }));
        }
    };

    const confirmFigura = () => {
        if (!figuraSetup.figuraId) return;
        addRiga({ descrizione: `${figuraSetup.descrizione} (${figuraSetup.ore}h)`, quantita: figuraSetup.ore, unita: 'ore', prezzoUnitario: figuraSetup.prezzoCliente });
        setActiveModal('NONE');
    };

    const confirmKm = () => {
        addRiga({ descrizione: "Rimborso chilometrico", quantita: tempKm, unita: 'km', prezzoUnitario: tariffaKm });
        setActiveModal('NONE');
    };

    // Generazione PDF: la logica jsPDF/autoTable è in `src/utils/generaPreventivoPdf.ts`,
    // usata anche lato Node per l'app così l'output è identico. Qui si raccolgono i
    // dati dallo stato, si chiama la utility e si gestisce download o blob.
    //
    // opts.docNumber: numero ufficiale già assegnato dal BE (PF-YYYY-NNNN / PV-YYYY-NNNN)
    // da stampare nel PDF. Se manca si usa il fallback PF-YYYY-MMDD / PR-YYYY-MMDD.
    const generatePdf = async (
        type: 'preventivo' | 'proforma',
        opts: { download?: boolean; docNumber?: string } = { download: true },
    ): Promise<{ blob: Blob; filename: string } | void> => {
        const result = await generaPreventivoPdf({
            type,
            settings: globalSettings,
            clientePdf,
            voci,
            note,
            ivaPercentuale,
            statoPreventivo,
            docNumber: opts.docNumber,
            nascondiTotale,
            nascondiIva,
        });
        if (!result) return;

        // Qui la proforma si scarica/invia soltanto, senza salvarla sul record;
        // l'eventuale upload lo decide il chiamante con result.blob.
        if (opts.download === false) {
            return result;
        }

        // download:true scarica il blob con il nome file standard.
        const url = URL.createObjectURL(result.blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = result.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 10000); // Safari annulla il download se si revoca subito
    };

    // Sconto per riga tra 0 e 100%: oltre, la voce andrebbe in negativo (il server rifiuta comunque).
    const scontiNonValidi = (): boolean => {
        const bad = voci.some(v => { const sc = Number(v.scontoPercentuale) || 0; return sc < 0 || sc > 100; });
        if (bad) alert("Lo sconto di una voce deve essere compreso tra 0% e 100%.");
        return bad;
    };

    // true = si può salvare. Con un altro cliente il preventivo perde il collegamento al
    // mandato/servizio del cliente precedente: lo si dice prima.
    const confermaScollegamento = (): boolean => {
        if (!id || !collegamento) return true;
        const clientIdNuovo = clientMode === 'existing' ? (selectedClient?.id ?? null) : null;
        if (clientIdNuovo === collegamento.clientId) return true;
        const ok = window.confirm(`Questo preventivo è collegato ${collegamento.descr}, del cliente precedente.

Cambiando cliente verrà scollegato: potrai ricollegarlo dalla lista a un mandato del nuovo cliente. Continuare?`);
        if (ok) setCollegamento(null);
        return ok;
    };

    // Salvataggio su DB
    const handleSavePreventivo = async () => {
        if (scontiNonValidi()) return;
        if (!confermaScollegamento()) return;
        if (!selectedClient && clientMode === 'existing') {
            alert("Seleziona un cliente prima di salvare.");
            return;
        }
        if (voci.length === 0) {
            alert("Inserisci almeno una voce nel preventivo.");
            return;
        }

        setIsSaving(true);
        try {
            const payload = {
                // In modifica includiamo l'id
                id: id ? Number(id) : undefined,

                clientMode: clientMode,
                clientId: clientMode === 'existing' ? selectedClient?.id : null,
                sedeId: sedeSelezionata,
                clientePdfData: clientePdf,
                newClientData: clientMode === 'new' ? { nome: clientePdf.denominazione, email: clientePdf.email, telefono: clientePdf.telefono, indirizzo: clientePdf.indirizzo, citta: clientePdf.citta, cap: clientePdf.cap, provincia: clientePdf.provincia, codiceFiscale: clientePdf.cfPiva } : null,

                vociJson: JSON.stringify(voci),
                imponibile: totali.imponibile,
                ivaValore: totali.iva,
                ivaPercentuale: ivaPercentuale,
                totale: totali.totale,
                notePerCliente: note,
                stato: statoPreventivo, // Stato scelto nel dropdown
                nascondiTotale,
                nascondiIva,
            };

            // Un download/condivisione in questa sessione può aver già creato il record:
            // lo aggiorno invece di crearne un doppione.
            const existingId = id ?? persistedIds.PREVENTIVO;
            if (existingId) {
                // Update
                await PreventiviService.update(existingId, { ...payload, id: Number(existingId) });
                addToast("Preventivo aggiornato con successo!", "success");
            } else {
                // Create
                await PreventiviService.create(payload);
                addToast("Preventivo creato con successo!", "success");
            }
            navigate('/preventivi');
        } catch (error) {
            console.error("Errore salvataggio preventivo:", error);
            addToast("Errore durante il salvataggio.", "error");
        } finally {
            setIsSaving(false);
        }
    };

    // Crea (o in modifica aggiorna) il record con il tipoDocumento richiesto e
    // restituisce { id, numeroProgressivo }. Va chiamato prima di generare il PDF,
    // così il numero assegnato dal BE (PF-YYYY-NNNN / PV-YYYY-NNNN) è lo stesso nel
    // PDF e in lista. In modifica il BE mantiene il numero esistente; in creazione
    // ne assegna uno nuovo.
    const persistRecordForPdf = async (
        tipoDocumento: 'PREVENTIVO' | 'PROFORMA',
    ): Promise<{ id: number; numeroProgressivo?: string } | null> => {
        try {
            // In modifica vince l'id dell'URL, altrimenti riusiamo quello già creato in
            // questa sessione per lo stesso tipo.
            const effectiveId: number | undefined = id ? Number(id) : persistedIds[tipoDocumento];
            const payload: any = {
                id: effectiveId,
                clientMode,
                clientId: clientMode === 'existing' ? selectedClient?.id : null,
                sedeId: sedeSelezionata,
                clientePdfData: clientePdf,
                newClientData: clientMode === 'new' ? {
                    nome: clientePdf.denominazione,
                    email: clientePdf.email,
                    telefono: clientePdf.telefono,
                    indirizzo: clientePdf.indirizzo,
                    citta: clientePdf.citta,
                    cap: clientePdf.cap,
                    provincia: clientePdf.provincia,
                    codiceFiscale: clientePdf.cfPiva,
                } : null,
                vociJson: JSON.stringify(voci),
                imponibile: totali.imponibile,
                ivaValore: totali.iva,
                ivaPercentuale: ivaPercentuale,
                totale: totali.totale,
                notePerCliente: note,
                stato: statoPreventivo,
                tipoDocumento,
                nascondiTotale,
                nascondiIva,
            };
            let dto: any;
            if (effectiveId) {
                const res = await PreventiviService.update(effectiveId, payload);
                dto = (res as any)?.data ?? res;
            } else {
                const res = await PreventiviService.create(payload);
                dto = (res as any)?.data ?? res;
            }
            if (!dto?.id) return null;
            // Solo in creazione: ricorda l'id così i prossimi download dello stesso tipo
            // aggiornano invece di duplicare.
            if (!id) setPersistedIds(prev => ({ ...prev, [tipoDocumento]: dto.id }));
            return { id: dto.id, numeroProgressivo: dto.numeroProgressivo };
        } catch (e) {
            console.warn('[persistRecordForPdf] errore:', e);
            return null;
        }
    };

    // Record su cui stampare/archiviare il PDF:
    // - preventivo, o proforma aperta dalla lista → il record stesso;
    // - proforma da un preventivo → salvo il preventivo e chiedo al BE la proforma
    // derivata, record PROFORMA a sé, riusata se il contenuto non è cambiato.
    const persistForPdf = async (
        type: 'preventivo' | 'proforma',
    ): Promise<{ id: number; numeroProgressivo?: string; reused?: boolean } | null> => {
        if (type === 'preventivo' || loadedTipo === 'PROFORMA') {
            return persistRecordForPdf(type === 'proforma' ? 'PROFORMA' : 'PREVENTIVO');
        }
        const src = await persistRecordForPdf('PREVENTIVO');
        if (!src?.id) return null;
        try {
            const res: any = await PreventiviService.proformaDaPreventivo(src.id, nascondiIva);
            const pf = res?.data ?? res;
            if (!pf?.id) return null;
            return { id: pf.id, numeroProgressivo: pf.numeroProgressivo, reused: !!pf.reused };
        } catch (e) {
            console.warn('[persistForPdf] proforma da preventivo fallita:', e);
            return null;
        }
    };

    // Senza record salvato niente PDF: avrebbe un numero che nel gestionale non esiste.
    const avvisaRecordNonSalvato = (type: 'preventivo' | 'proforma') =>
        addToast(`${type === 'proforma' ? 'La proforma non è stata salvata' : 'Il preventivo non è stato salvato'}, quindi il PDF non è stato generato. Riprova.`, 'error');

    // Prima crea/aggiorna il record per avere il numero ufficiale, poi genera il PDF
    // e lo carica sul record. Una proforma riusata tiene il PDF già archiviato.
    const handleDownloadDoc = async (type: 'preventivo' | 'proforma') => {
        if (downloadingDoc) return;
        if (scontiNonValidi()) return;
        if (!confermaScollegamento()) return;
        setDownloadingDoc(type);
        try {
            const rec = await persistForPdf(type);
            if (!rec?.id) { avvisaRecordNonSalvato(type); return; }
            const result = await generatePdf(type, {
                download: true,
                docNumber: rec.numeroProgressivo,
            });
            if (result && !rec.reused) {
                try { await PreventiviService.savePdf(rec.id, result.blob, result.filename); }
                catch (e) { console.warn('[handleDownloadDoc] upload PDF fallito', e); }
            }
        } finally {
            setDownloadingDoc(null);
        }
    };

    // Condivisione: tipo di documento e blob già generato (anteprima in SHARE_OPTIONS).
    const [sharePayload, setSharePayload] = useState<{ type: 'preventivo' | 'proforma'; blob: Blob; filename: string } | null>(null);
    const [isPreparingShare, setIsPreparingShare] = useState(false);

    const handleShare = () => {
        // Step 1: l'utente sceglie quale documento condividere
        setActiveModal('SHARE_TYPE');
    };

    // Step 2: genera il PDF e prova la condivisione nativa con file. Se non è
    // disponibile o viene annullata, mostra SHARE_OPTIONS (anteprima, download,
    // link a WhatsApp/Telegram/Email). Il record si crea/aggiorna prima, come per il download.
    const handleSharePdf = async (type: 'preventivo' | 'proforma') => {
        if (scontiNonValidi()) return;
        if (!confermaScollegamento()) return;
        setIsPreparingShare(true);
        try {
            const rec = await persistForPdf(type);
            if (!rec?.id) { avvisaRecordNonSalvato(type); return; }
            const result = await generatePdf(type, {
                download: false,
                docNumber: rec.numeroProgressivo,
            });
            if (!result) return;
            const { blob, filename } = result;
            if (!rec.reused) {
                try { await PreventiviService.savePdf(rec.id, blob, filename); }
                catch (e) { console.warn('[handleSharePdf] upload PDF fallito', e); }
            }
            const file = new File([blob], filename, { type: 'application/pdf' });
            setSharePayload({ type, blob, filename });

            // Condivisione nativa (mobile)
            const nav = navigator as any;
            if (nav.canShare && nav.canShare({ files: [file] })) {
                try {
                    await nav.share({
                        title: type === 'proforma' ? 'Fattura Proforma' : 'Preventivo',
                        text: `Totale ${type === 'proforma' ? 'proforma' : 'preventivo'}: ${fmtEur(totali.totale)}`,
                        files: [file],
                    });
                    setActiveModal('NONE');
                    return;
                } catch (err) {
                    // Annullato dall'utente o rifiutato dal browser: si passa a SHARE_OPTIONS
                }
            }

            setActiveModal('SHARE_OPTIONS');
        } catch (err) {
            console.error('[handleSharePdf]', err);
            alert('Errore nella generazione del PDF.');
        } finally {
            setIsPreparingShare(false);
        }
    };

    const getShareLink = (type: 'wa' | 'email' | 'telegram') => {
        const phone = clientePdf.telefono;
        const email = clientePdf.email;
        const docKind = sharePayload?.type === 'proforma' ? 'fattura proforma' : 'preventivo';
        const filename = sharePayload?.filename || '';
        // WhatsApp/Telegram/Email web non permettono allegati via URL: nel messaggio
        // indichiamo il nome del PDF scaricato da allegare a mano.
        const text = encodeURIComponent(
            `Gentile cliente, in allegato la ${docKind} (totale ${fmtEur(totali.totale)}).${filename ? `\n\nFile: ${filename}` : ''}`
        );

        switch(type) {
            case 'wa': return `https://wa.me/${phone?.replace(/[^0-9]/g, '')}?text=${text}`;
            case 'email': return `mailto:${email}?subject=${encodeURIComponent(sharePayload?.type === 'proforma' ? 'Fattura Proforma' : 'Preventivo')}&body=${text}`;
            case 'telegram': return `https://t.me/share/url?url=&text=${text}`;
            default: return '#';
        }
    };

    // Scarica il blob già generato (fallback da SHARE_OPTIONS).
    const downloadSharedPdf = () => {
        if (!sharePayload) return;
        const url = URL.createObjectURL(sharePayload.blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = sharePayload.filename;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 10000); // Safari annulla il download se si revoca subito
    };

    if (caricamento) {
        return (
            <div className="flex flex-col items-center justify-center gap-3 py-32 text-gray-500">
                <Loader2 className="animate-spin text-blue-600" size={32} />
                <p className="text-sm font-medium">Caricamento preventivo…</p>
            </div>
        );
    }

    return (
        <div className="p-4 lg:p-6 max-w-6xl mx-auto !pb-0" ref={formRootRef}>

            {/* Header */}
            <div className="flex justify-between items-end mb-8">
                <div>
                    <div className="flex items-center gap-2 text-gray-500 hover:text-gray-900 cursor-pointer w-fit mb-2" onClick={() => navigate('/preventivi')}>
                        <ChevronLeft size={20}/> Torna alla lista
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <FileSpreadsheet className="text-blue-600" />
                        {id ? "Modifica Preventivo" : (globalSettings?.enableProforma ? "Nuova Fattura Proforma" : "Nuovo Preventivo")}
                    </h1>
                    <p className="text-sm text-gray-500">
                        {step === 1 ? "Selezione Cliente" : "Redazione Conteggio Economico"}
                    </p>
                </div>

                <div className="flex items-center gap-4">
                    {/* Selettore stato (solo step 2) */}
                    {step === 2 && (
                        <div className="flex flex-col items-end">
                            <label className="text-xs font-bold text-gray-500 uppercase mb-1">Stato</label>
                            <select
                                value={statoPreventivo}
                                onChange={(e) => setStatoPreventivo(e.target.value)}
                                className={`p-2 rounded-lg border font-bold text-sm outline-none cursor-pointer ${
                                    statoPreventivo === 'ACCETTATO' ? 'bg-green-50 text-green-700 border-green-200' :
                                        statoPreventivo === 'RIFIUTATO' ? 'bg-red-50 text-red-700 border-red-200' :
                                            statoPreventivo === 'INVIATO' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                                'bg-white text-gray-700 border-gray-300'
                                }`}
                            >
                                <option value="BOZZA">BOZZA</option>
                                <option value="INVIATO">INVIATO</option>
                                <option value="ACCETTATO">ACCETTATO</option>
                                <option value="RIFIUTATO">RIFIUTATO</option>
                            </select>
                        </div>
                    )}

                    {/* Step Indicator */}
                    <div className="flex gap-2 h-fit self-end pb-2">
                        <div className={`h-2 w-12 rounded-full ${step >= 1 ? 'bg-blue-600' : 'bg-gray-200'}`} />
                        <div className={`h-2 w-12 rounded-full ${step >= 2 ? 'bg-blue-600' : 'bg-gray-200'}`} />
                    </div>
                </div>
            </div>

            {/* Step 1: cliente */}
            {step === 1 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 lg:p-8 max-w-3xl mx-auto animate-in fade-in slide-in-from-bottom-4">
                    <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                        <User className="text-blue-600" size={20}/> Intestatario Documento
                    </h2>

                    <div className="flex bg-gray-100 p-1 rounded-lg mb-6 w-fit">
                        <button onClick={() => setClientMode('existing')} className={`px-4 py-2 text-sm font-medium rounded ${clientMode === 'existing' ? 'bg-white shadow text-blue-600' : 'text-gray-500'}`}>Cliente in Rubrica</button>
                        <button onClick={() => setClientMode('new')} className={`px-4 py-2 text-sm font-medium rounded ${clientMode === 'new' ? 'bg-white shadow text-blue-600' : 'text-gray-500'}`}>Nuovo Contatto</button>
                    </div>

                    {clientMode === 'existing' ? (
                        <div className="space-y-3">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                <input autoFocus type="text" placeholder="Cerca cliente..." className="w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                            </div>
                            <div className="max-h-48 overflow-y-auto space-y-2">
                                {filteredClients.map(c => (
                                    <div key={c.id} onClick={() => {
                                        setSelectedClient(c);
                                        // Per le aziende email e telefono vengono dal referente, con fallback sui
                                        // campi nativi per i clienti più vecchi.
                                        setClientePdf({
                                            denominazione: getClientDisplayName(c) || `${c.nome || ''} ${c.surname || ''}`.trim(),
                                            indirizzo: c.indirizzo || '',
                                            citta: c.citta || '',
                                            cap: c.cap || '',
                                            provincia: c.provincia || '',
                                            cfPiva: c.codiceFiscale || c.partitaIva || '',
                                            email: getClientEmail(c),
                                            telefono: getClientTelefono(c),
                                        });
                                    }} className={`p-3 rounded-xl border cursor-pointer flex items-center gap-3 ${selectedClient?.id === c.id ? 'border-blue-500 bg-blue-50' : 'hover:bg-gray-50'}`}>
                                        <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-500">
                                            {c.ragioneSociale ? <Building2 size={18}/> : <User size={18}/>}
                                        </div>
                                        <div className="flex-1">
                                            <div className="font-bold text-gray-900">
                                                {isClienteAzienda(c) && getClientReferente(c)
                                                    ? `${getClientDisplayName(c)} (${getClientReferente(c)})`
                                                    : getClientDisplayName(c)}
                                            </div>
                                            <div className="text-xs text-gray-500">{c.email}</div>
                                        </div>
                                        {selectedClient?.id === c.id && <CheckCircle className="text-blue-600" size={20}/>}
                                    </div>
                                ))}
                            </div>
                            {selectedClient && (
                                <div className="border-t border-gray-100 pt-4">
                                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
                                        Dati per il documento <span className="normal-case font-normal">(modificabili — non aggiornano la rubrica)</span>
                                    </p>
                                    <ClientePdfForm data={clientePdf} onChange={updateClientePdf} />
                                </div>
                            )}
                        </div>
                    ) : (
                        <ClientePdfForm data={clientePdf} onChange={updateClientePdf} />
                    )}
                </div>
            )}

            {/* Step 2: editor */}
            {step === 2 && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-8 animate-in fade-in slide-in-from-right-4">

                    {/* Voci */}
                    <div className="lg:col-span-2 space-y-6">
                        <div className="flex flex-wrap gap-2">
                            <button onClick={() => { setFiguraSetup({ figuraId: '', descrizione: '', ore: 4, costoInterno: 0, prezzoCliente: 0 }); setActiveModal('COLLAB'); }} className="flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 border border-indigo-200"><Users size={18} /> + Figura Professionale</button>
                            <button onClick={() => setActiveModal('KM')} className="flex items-center gap-2 px-4 py-2 bg-orange-50 text-orange-700 rounded-lg hover:bg-orange-100 border border-orange-200"><Car size={18} /> + Rimborso Km</button>
                            <button onClick={() => setActiveModal('SERVICES')} className="flex items-center gap-2 px-4 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 border border-purple-200"><List size={18} /> + Da Listino</button>
                            <button onClick={() => addRiga({ descrizione: '', prezzoUnitario: 0 })} className="flex items-center gap-2 px-4 py-2 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 border border-gray-200"><Plus size={18} /> Riga Libera</button>
                        </div>

                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                            <div className="overflow-x-auto -mx-2 px-2">
                            <table className="w-full text-sm text-left">
                                <thead className="bg-gray-50 text-gray-500 font-bold border-b bg-gray-50/50">
                                <tr>
                                    <th className="p-3 w-8"></th>
                                    <th className="p-3 pl-2">Descrizione</th>
                                    <th className="p-3 w-20 text-center">Q.tà</th>
                                    <th className="p-3 w-20">Unità</th>
                                    <th className="p-3 w-24 text-right">Prezzo</th>
                                    <th className="p-3 w-20 text-center">Sconto%</th>
                                    <th className="p-3 w-28 text-right">Totale</th>
                                    <th className="p-3 w-24"></th>
                                </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                {voci.map((riga, index) => {
                                    const calc = calcolaVoce(riga, globalSettings);
                                    const isExpanded = expandedVoci.has(riga.id);
                                    const hasMagg = calc.maggiorazioni.length > 0;
                                    return (
                                        <React.Fragment key={riga.id}>
                                            <tr className="hover:bg-blue-50/30 group transition-colors">
                                                <td className="p-1 text-center">
                                                    <button
                                                        onClick={() => toggleExpanded(riga.id)}
                                                        className={`p-1 rounded hover:bg-blue-100 transition-colors ${hasMagg ? 'text-amber-600' : 'text-gray-300 hover:text-gray-500'}`}
                                                        title={hasMagg ? `${calc.maggiorazioni.length} maggiorazioni applicate` : 'Apri pannello maggiorazioni'}
                                                    >
                                                        {isExpanded ? <ChevronDown size={16}/> : <ChevronRight size={16}/>}
                                                    </button>
                                                </td>
                                                <td className="p-2 pl-2">
                                                    <input type="text" className="w-full bg-transparent border-b border-transparent focus:border-blue-500 outline-none font-medium" value={riga.descrizione} onChange={e => updateRiga(riga.id, 'descrizione', e.target.value)}/>
                                                    {hasMagg && (
                                                        <div className="flex flex-wrap gap-1 mt-1">
                                                            {calc.maggiorazioni.map((m, idx) => (
                                                                <span key={idx} className="inline-flex items-center gap-1 text-[10px] font-bold bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full border border-amber-200">
                                                                    <Percent size={9}/> {m.label} +{m.percentuale}%
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="p-2"><NumberInput
                                                                           min={0} className="w-full bg-transparent text-center border-b border-transparent focus:border-blue-500 outline-none" value={riga.quantita} onChange={e => updateRiga(riga.id, 'quantita', parseFloat(e.target.value))}/></td>
                                                <td className="p-2"><select className="w-full bg-transparent text-xs outline-none" value={riga.unita} onChange={e => updateRiga(riga.id, 'unita', e.target.value)}><option value="ore">ore</option><option value="gg">gg</option><option value="km">km</option><option value="forfait">forf.</option><option value="n.">n.</option></select></td>
                                                <td className="p-2"><NumberInput
                                                                           min={0} className="w-full bg-transparent text-right border-b border-transparent focus:border-blue-500 outline-none font-mono" value={riga.prezzoUnitario} onChange={e => updateRiga(riga.id, 'prezzoUnitario', parseFloat(e.target.value))}/></td>
                                                <td className="p-2"><NumberInput
                                                                           min={0} max={100} title="Sconto massimo 100%" className={`w-full text-center text-red-500 border-b outline-none ${(Number(riga.scontoPercentuale) || 0) > 100 ? 'border-red-500 bg-red-50' : 'bg-transparent border-transparent focus:border-red-500'}`} value={riga.scontoPercentuale} onChange={e => updateRiga(riga.id, 'scontoPercentuale', parseFloat(e.target.value))}/>
                                                    {(Number(riga.scontoPercentuale) || 0) > 100 && (<div className="text-[10px] font-semibold text-red-600 text-center mt-0.5">Max 100%</div>)}</td>
                                                <td className="p-3 text-right font-mono font-bold text-gray-900">
                                                    {fmtEur(calc.totale)}
                                                    {riga.scontoPercentuale > 0 ? (
                                                        <div className="text-[10px] font-normal text-gray-400 line-through">
                                                            {fmtEur((calc.subtotaleBase * (1 + calc.percentualeTotale / 100)))}
                                                        </div>
                                                    ) : hasMagg ? (
                                                        <div className="text-[10px] font-normal text-gray-400 line-through">{fmtEur(calc.subtotaleBase)}</div>
                                                    ) : null}
                                                </td>
                                                <td className="p-2">
                                                    <div className="flex items-center justify-end gap-1">
                                                        <button type="button" onClick={() => spostaRiga(index, -1)} disabled={index === 0}
                                                                title="Sposta su" aria-label="Sposta su"
                                                                className="p-1 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 disabled:opacity-25 disabled:pointer-events-none"><ChevronUp size={16} /></button>
                                                        <button type="button" onClick={() => spostaRiga(index, 1)} disabled={index === voci.length - 1}
                                                                title="Sposta giù" aria-label="Sposta giù"
                                                                className="p-1 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 disabled:opacity-25 disabled:pointer-events-none"><ChevronDown size={16} /></button>
                                                        <button type="button" onClick={() => removeRiga(riga.id)} title="Elimina riga" aria-label="Elimina riga"
                                                                className="p-1 rounded text-gray-300 hover:text-red-500"><Trash2 size={16} /></button>
                                                    </div>
                                                </td>
                                            </tr>
                                            {isExpanded && (
                                                <tr className="bg-amber-50/40 border-y border-amber-100">
                                                    <td colSpan={8} className="p-4">
                                                        <div className="flex items-center gap-2 mb-3 pb-2 border-b border-amber-200">
                                                            <Sliders size={14} className="text-amber-600"/>
                                                            <span className="text-xs font-bold text-amber-800 uppercase tracking-wider">Maggiorazioni — calcolo automatico</span>
                                                        </div>
                                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                                                            {/* Notte / Festivo */}
                                                            <div className="space-y-1.5">
                                                                <label className="block font-bold text-gray-600">Data servizio</label>
                                                                <input type="date" className="w-full p-1.5 border rounded text-xs"
                                                                    value={riga.dataServizio || ''}
                                                                    onChange={e => updateRiga(riga.id, 'dataServizio', e.target.value)}/>
                                                                <p className="text-[10px] text-gray-400 italic">Domenica/festivo → maj. Festivo</p>
                                                            </div>
                                                            <div className="space-y-1.5">
                                                                <label className="block font-bold text-gray-600">Ora inizio</label>
                                                                <input type="time" className="w-full p-1.5 border rounded text-xs"
                                                                    value={riga.oraInizio || ''}
                                                                    onChange={e => updateRiga(riga.id, 'oraInizio', e.target.value)}/>
                                                            </div>
                                                            <div className="space-y-1.5">
                                                                <label className="block font-bold text-gray-600">Ora fine</label>
                                                                <input type="time" className="w-full p-1.5 border rounded text-xs"
                                                                    value={riga.oraFine || ''}
                                                                    onChange={e => updateRiga(riga.id, 'oraFine', e.target.value)}/>
                                                                <p className="text-[10px] text-gray-400 italic">In fascia notturna → maj. Notte</p>
                                                            </div>
                                                            <div className="space-y-1.5">
                                                                <label className="block font-bold text-gray-600">Km dalla sede</label>
                                                                <NumberInput
                                                                       min={0} placeholder="0" className="w-full p-1.5 border rounded text-xs text-right"
                                                                    value={riga.kmDallaSede ?? ''}
                                                                    onChange={e => updateRiga(riga.id, 'kmDallaSede', e.target.value === '' ? undefined : parseFloat(e.target.value))}/>
                                                                <p className="text-[10px] text-gray-400 italic">50/100/150 km → fascia distanza</p>
                                                            </div>

                                                            <div className="space-y-1.5 col-span-2">
                                                                <label className="block font-bold text-gray-600">Inizio servizio richiesto (urgenza attivazione)</label>
                                                                <input type="datetime-local" className="w-full p-1.5 border rounded text-xs"
                                                                    value={riga.dataInizioRichiesta || ''}
                                                                    onChange={e => updateRiga(riga.id, 'dataInizioRichiesta', e.target.value)}/>
                                                                <p className="text-[10px] text-gray-400 italic">Calcolata sulla differenza con "ora attuale" (≤12h / ≤24h / ≤48h)</p>
                                                            </div>
                                                            <div className="space-y-1.5 col-span-2">
                                                                <label className="block font-bold text-gray-600">Scadenza relazione (urgenza relazione)</label>
                                                                <input type="datetime-local" className="w-full p-1.5 border rounded text-xs"
                                                                    value={riga.dataScadenzaRelazione || ''}
                                                                    onChange={e => updateRiga(riga.id, 'dataScadenzaRelazione', e.target.value)}/>
                                                                <p className="text-[10px] text-gray-400 italic">≤24h o ≤48h da adesso</p>
                                                            </div>
                                                        </div>

                                                        {/* Breakdown */}
                                                        <div className="mt-4 pt-3 border-t border-amber-200 grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                                                            <div className="bg-white rounded p-2 border border-amber-100">
                                                                <span className="text-gray-500">Subtotale base:</span>
                                                                <span className="float-right font-mono font-bold text-gray-700">{fmtEur(calc.subtotaleBase)}</span>
                                                            </div>
                                                            <div className="bg-white rounded p-2 border border-amber-100">
                                                                <span className="text-gray-500">Maggiorazione totale:</span>
                                                                <span className="float-right font-mono font-bold text-amber-600">+{calc.percentualeTotale}%</span>
                                                            </div>
                                                            <div className="bg-amber-100 rounded p-2 border border-amber-200">
                                                                <span className="text-amber-900 font-bold">Totale voce:</span>
                                                                <span className="float-right font-mono font-black text-amber-900">{fmtEur(calc.totale)}</span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                                </tbody>
                            </table>
                            </div>
                        </div>

                        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
                            <label className="text-xs font-bold text-gray-500 uppercase mb-2 block">Note</label>
                            <textarea className="w-full p-3 border rounded-lg text-sm h-24 resize-none focus:ring-2 focus:ring-blue-500 outline-none" value={note} onChange={e => setNote(e.target.value)}/>
                        </div>
                    </div>

                    {/* Riepilogo */}
                    <div className="space-y-6">
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 lg:p-6 sticky top-6">
                            <h3 className="font-bold text-gray-900 text-lg mb-4 flex items-center gap-2"><Calculator size={20} className="text-blue-600"/> Totale Bozza</h3>

                            <div className={`space-y-3 mb-4 pb-6 border-b border-gray-100 ${nascondiTotale ? 'opacity-40' : ''}`}>
                                <div className="flex justify-between text-sm text-gray-600"><span>Imponibile</span><span className="font-mono">{fmtEur(totali.imponibile)}</span></div>
                                <div className="flex justify-between text-sm text-gray-600 items-center">
                                    {/* IVA fissa al 22%; per non mostrarla c'è il toggle "Non mostrare l'IVA". */}
                                    <span>IVA 22%</span>
                                    <span className="font-mono">{fmtEur(totali.iva)}</span>
                                </div>
                                <div className="flex justify-between text-xl font-extrabold text-gray-900 mt-2"><span>Totale</span><span>{fmtEur(totali.totale)}</span></div>
                            </div>

                            {/*
                              Nascondi totale, solo per il preventivo (la proforma mostra sempre il totale).
                              Vale per PDF e dettaglio; qui resta visibile in grigio.
                            */}
                            <label className="flex items-start gap-2 mb-5 cursor-pointer select-none">
                                <input type="checkbox" checked={nascondiTotale} onChange={e => setNascondiTotale(e.target.checked)} className="mt-0.5 h-4 w-4 accent-blue-600" />
                                <span className="text-xs text-gray-600 leading-snug">
                                    <span className="font-bold text-gray-800">Nascondi il totale nel preventivo</span><br/>
                                    Il box totali (Imponibile/IVA/Totale) non compare nel PDF né nella vista del preventivo inviato al cliente. Le voci restano coi loro importi. Non si applica alle proforma.
                                </span>
                            </label>

                            {/* Non mostrare IVA, per preventivo e proforma: nel box solo il totale imponibile. */}
                            <label className="flex items-start gap-2 mb-5 cursor-pointer select-none">
                                <input type="checkbox" checked={nascondiIva} onChange={e => setNascondiIva(e.target.checked)} className="mt-0.5 h-4 w-4 accent-blue-600" />
                                <span className="text-xs text-gray-600 leading-snug">
                                    <span className="font-bold text-gray-800">Non mostrare l'IVA</span><br/>
                                    Nel documento il box totali mostra un unico importo <b>Totale = imponibile</b> (nessuna riga IVA). Vale anche per le proforma.
                                </span>
                            </label>

                            <div className="grid grid-cols-2 gap-3">
                                {/* Salva su database */}
                                <button onClick={handleSavePreventivo} disabled={isSaving} className="col-span-2 flex items-center justify-center gap-2 w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold shadow-lg shadow-blue-100 transition-all">
                                    {isSaving ? <Loader2 className="animate-spin" size={20}/> : <Save size={20} />}
                                    {id ? "Aggiorna Preventivo" : "Salva su Gestionale"}
                                </button>

                                <button onClick={handleShare} className="col-span-2 flex items-center justify-center gap-2 w-full py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold shadow-lg shadow-green-100 transition-all">
                                    <Share2 size={20} /> Invia / Condividi
                                </button>

                                {/* Pulsanti separati per preventivo e proforma */}
                                <button onClick={() => handleDownloadDoc('preventivo')} disabled={!!downloadingDoc} className="flex items-center justify-center gap-2 w-full py-3 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-xl font-bold transition-all text-sm disabled:opacity-60 disabled:cursor-not-allowed">
                                    {downloadingDoc === 'preventivo' ? <Loader2 className="animate-spin" size={18}/> : <Download size={18} />} Preventivo
                                </button>

                                <button onClick={() => handleDownloadDoc('proforma')} disabled={!!downloadingDoc} className="flex items-center justify-center gap-2 w-full py-3 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-xl font-bold transition-all text-sm disabled:opacity-60 disabled:cursor-not-allowed">
                                    {downloadingDoc === 'proforma' ? <Loader2 className="animate-spin" size={18}/> : <Download size={18} />} Proforma
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Footer */}
            {/* Sticky dentro la pagina e non fixed sullo schermo: con la sidebar aperta resta visibile. */}
            <div className="sticky bottom-0 -mx-4 lg:-mx-6 mt-8 bg-white border-t p-4 z-20 flex justify-center shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
                <div className="flex gap-4 w-full max-w-5xl justify-between px-2 lg:px-0">
                    {step === 2 ? (
                        <button onClick={() => setStep(1)} className="px-5 py-2 text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg font-semibold flex items-center gap-2 transition-colors">
                            <User size={18} /> Modifica cliente
                        </button>
                    ) : (
                        <button onClick={() => navigate('/preventivi')} className="px-6 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-medium transition-colors">Indietro</button>
                    )}
                    {step === 1 && <button onClick={() => setStep(2)} className="bg-blue-600 text-white px-8 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 font-bold shadow-lg transition-all">{id ? 'Torna alle voci' : 'Crea Bozza'} <ChevronLeft size={20} className="rotate-180" /></button>}
                </div>
            </div>

            {/* Modali */}
            {activeModal === 'COLLAB' && (
                <Modal title="Aggiungi Figura Professionale" onClose={() => setActiveModal('NONE')}>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">Figura Professionale</label>
                            <select
                                className="w-full p-2.5 border rounded-lg bg-white text-sm font-medium outline-none focus:ring-2 focus:ring-blue-500"
                                value={String(figuraSetup.figuraId)}
                                onChange={e => onSelectFigura(e.target.value)}
                            >
                                <option value="">— Seleziona figura —</option>
                                {figureProfessionali.map(f => (
                                    <option key={f.id} value={String(f.id)}>{f.nome}</option>
                                ))}
                            </select>
                        </div>

                        {figuraSetup.figuraId && (
                            <>
                                <div className="bg-gray-50 rounded-xl p-3 flex items-center justify-between border border-gray-200">
                                    <div>
                                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Costo Interno</p>
                                        <p className="text-sm font-black text-gray-700">{fmtEur(figuraSetup.costoInterno)} / ora</p>
                                    </div>
                                    <span className="text-[10px] bg-gray-200 text-gray-600 font-bold px-2 py-1 rounded-md">Non visibile al cliente</span>
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">Prezzo Cliente (€/ora)</label>
                                    <NumberInput min="0" step="0.5" className="w-full p-2.5 border rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500 outline-none"
                                        value={figuraSetup.prezzoCliente}
                                        onChange={e => setFiguraSetup(prev => ({ ...prev, prezzoCliente: parseFloat(e.target.value) || 0 }))}/>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">Ore</label>
                                        <NumberInput min="0.5" step="0.5" className="w-full p-2.5 border rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500 outline-none"
                                            value={figuraSetup.ore}
                                            onChange={e => setFiguraSetup(prev => ({ ...prev, ore: parseFloat(e.target.value) || 0 }))}/>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">Descrizione riga</label>
                                        <input type="text" className="w-full p-2.5 border rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500 outline-none"
                                            value={figuraSetup.descrizione}
                                            onChange={e => setFiguraSetup(prev => ({ ...prev, descrizione: e.target.value }))}/>
                                    </div>
                                </div>

                                <div className="bg-blue-50 rounded-xl p-3 flex items-center justify-between border border-blue-100">
                                    <span className="text-sm font-bold text-blue-700">Totale riga cliente</span>
                                    <span className="text-lg font-black text-blue-700">{fmtEur((figuraSetup.ore * figuraSetup.prezzoCliente))}</span>
                                </div>
                            </>
                        )}

                        <div className="flex justify-end gap-2 pt-2">
                            <button onClick={() => setActiveModal('NONE')} className="px-4 py-2 text-gray-600 rounded-lg hover:bg-gray-100">Annulla</button>
                            <button onClick={confirmFigura} disabled={!figuraSetup.figuraId}
                                className="px-6 py-2 bg-blue-600 text-white rounded-lg font-bold disabled:opacity-50">Aggiungi Riga</button>
                        </div>
                    </div>
                </Modal>
            )}

            {activeModal === 'KM' && (
                <Modal title="Rimborso KM" onClose={() => setActiveModal('NONE')}>
                    <div className="space-y-4">
                        <label className="block text-sm font-medium">Km da rimborsare</label>
                        <NumberInput
                               min={0} className="w-full p-3 border rounded-lg text-lg" value={tempKm} onChange={e => setTempKm(parseFloat(e.target.value) || 0)} autoFocus />
                        <div className="text-right text-gray-500 text-sm">Tariffa: €{tariffaKm}/km</div>
                        <div className="flex justify-end pt-2"><button onClick={confirmKm} className="px-6 py-2 bg-blue-600 text-white rounded-lg font-bold">Aggiungi</button></div>
                    </div>
                </Modal>
            )}

            {activeModal === 'SERVICES' && (
                <Modal title="Da Listino" onClose={() => setActiveModal('NONE')}>
                    <div className="space-y-2 max-h-80 overflow-y-auto">
                        {activitiesList.map(act => (
                            <button key={act.id} onClick={() => { addRiga({ descrizione: act.name, quantita: 1, unita: 'forfait', prezzoUnitario: act.defaultPrice || 0 }); setActiveModal('NONE'); }} className="w-full text-left p-3 border rounded hover:bg-gray-50 flex justify-between">
                                <span>{act.name}</span><span className="font-bold text-blue-600">€ {act.defaultPrice}</span>
                            </button>
                        ))}
                    </div>
                </Modal>
            )}

            {activeModal === 'SHARE_TYPE' && (
                <Modal title="Invia preventivo o proforma" onClose={() => !isPreparingShare && setActiveModal('NONE')}>
                    <p className="text-xs text-gray-500 mb-4">Scegli il documento da generare. Lo invieremo come PDF.</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <button
                            onClick={() => handleSharePdf('preventivo')}
                            disabled={isPreparingShare}
                            className="text-left p-4 border-2 border-slate-200 hover:border-blue-500 hover:bg-blue-50/50 rounded-xl transition-all group disabled:opacity-50 disabled:cursor-wait"
                        >
                            <div className="flex items-center gap-2 mb-2">
                                <div className="p-2 bg-blue-100 text-blue-600 rounded-lg group-hover:bg-blue-600 group-hover:text-white transition-colors"><FileSpreadsheet size={16}/></div>
                                <span className="text-sm font-black text-slate-900">Preventivo</span>
                            </div>
                            <p className="text-[11px] text-slate-500 leading-relaxed">Documento di offerta commerciale prima dell'incarico.</p>
                        </button>
                        <button
                            onClick={() => handleSharePdf('proforma')}
                            disabled={isPreparingShare}
                            className="text-left p-4 border-2 border-slate-200 hover:border-emerald-500 hover:bg-emerald-50/50 rounded-xl transition-all group disabled:opacity-50 disabled:cursor-wait"
                        >
                            <div className="flex items-center gap-2 mb-2">
                                <div className="p-2 bg-emerald-100 text-emerald-600 rounded-lg group-hover:bg-emerald-600 group-hover:text-white transition-colors"><FileText size={16}/></div>
                                <span className="text-sm font-black text-slate-900">Fattura Proforma</span>
                            </div>
                            <p className="text-[11px] text-slate-500 leading-relaxed">Riepilogo non fiscale per il cliente. Stesse voci del preventivo.</p>
                        </button>
                    </div>
                    {isPreparingShare && (
                        <div className="mt-4 flex items-center justify-center gap-2 text-xs text-slate-500">
                            <Loader2 size={14} className="animate-spin"/> Generazione PDF in corso...
                        </div>
                    )}
                </Modal>
            )}

            {activeModal === 'SHARE_OPTIONS' && (
                <Modal title="Scegli canale di invio" onClose={() => setActiveModal('NONE')}>
                    {sharePayload && (
                        <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded-xl flex items-center gap-3">
                            <div className="p-2 bg-white text-slate-600 rounded-lg border border-slate-200"><FileText size={18}/></div>
                            <div className="flex-1 min-w-0">
                                <div className="text-xs font-bold text-slate-700 truncate">{sharePayload.filename}</div>
                                <div className="text-[10px] text-slate-400">{sharePayload.type === 'proforma' ? 'Fattura Proforma' : 'Preventivo'} pronto</div>
                            </div>
                            <button onClick={downloadSharedPdf}
                                    className="text-xs font-bold bg-slate-900 hover:bg-slate-800 text-white px-3 py-1.5 rounded-lg flex items-center gap-1.5 whitespace-nowrap">
                                <Download size={13}/> Scarica
                            </button>
                        </div>
                    )}
                    <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3 leading-relaxed">
                        <strong>Nota:</strong> WhatsApp Web, Telegram ed Email non permettono l'allegato automatico via link. Scarica prima il PDF qui sopra, poi allegalo manualmente nella conversazione che si aprirà.
                    </p>
                    <div className="grid grid-cols-1 gap-3">
                        <a href={getShareLink('wa')} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-4 bg-green-50 text-green-800 rounded-xl hover:bg-green-100 transition-colors font-bold border border-green-200">
                            <MessageCircle size={24}/> WhatsApp Web
                        </a>
                        <a href={getShareLink('telegram')} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-4 bg-blue-50 text-blue-800 rounded-xl hover:bg-blue-100 transition-colors font-bold border border-blue-200">
                            <Send size={24}/> Telegram
                        </a>
                        <a href={getShareLink('email')} className="flex items-center gap-3 p-4 bg-gray-50 text-gray-800 rounded-xl hover:bg-gray-100 transition-colors font-bold border border-gray-200">
                            <Mail size={24}/> Email
                        </a>
                    </div>
                </Modal>
            )}

        </div>
    );
};

const ClientePdfForm: React.FC<{
    data: { denominazione: string; indirizzo: string; citta: string; cap: string; provincia: string; cfPiva: string; email: string; telefono: string };
    onChange: (field: string, value: string) => void;
}> = ({ data, onChange }) => {
    const cls = "w-full p-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none";
    return (
        <div className="space-y-3">
            <div>
                <label className="block text-xs font-bold text-gray-600 mb-1">Nome / Cognome o Denominazione *</label>
                <input type="text" className={cls} placeholder="Es. Mario Rossi oppure Rossi S.r.l."
                       value={data.denominazione} onChange={e => onChange('denominazione', e.target.value)} />
            </div>
            <div className="relative">
                <MapPin className="absolute left-2.5 top-3 text-gray-400" size={14} />
                <input type="text" placeholder="Indirizzo" className={`${cls} pl-8`}
                       value={data.indirizzo} onChange={e => onChange('indirizzo', e.target.value)} />
            </div>
            <div className="grid grid-cols-3 gap-2">
                <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1">CAP</label>
                    <input type="text" className={cls} value={data.cap} onChange={e => onChange('cap', e.target.value)} />
                </div>
                <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1">Città</label>
                    <input type="text" className={cls} value={data.citta} onChange={e => onChange('citta', e.target.value)} />
                </div>
                <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1">Prov.</label>
                    <input type="text" maxLength={2} className={`${cls} uppercase`}
                           value={data.provincia} onChange={e => onChange('provincia', e.target.value)} />
                </div>
            </div>
            <div>
                <label className="block text-xs font-bold text-gray-600 mb-1">C.F. / P.IVA</label>
                <input type="text" className={`${cls} uppercase`}
                       value={data.cfPiva} onChange={e => onChange('cfPiva', e.target.value)} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1">Email</label>
                    <input type="email" className={cls} value={data.email} onChange={e => onChange('email', e.target.value)} />
                </div>
                <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1">Telefono</label>
                    <input type="tel" className={cls} value={data.telefono} onChange={e => onChange('telefono', e.target.value)} />
                </div>
            </div>
        </div>
    );
};

export default NuovoPreventivoView;